/**
 * API Endpoints - Centralized API calls for the entire application
 * All API endpoints should be defined here for consistency and maintainability
 */

import api from './api';

// ============================================================================
// AUTH API - Authentication and user management
// ============================================================================
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getUser: () => api.get('/user'),
};

// ============================================================================
// TRENDS API - Trending topics, keyword analysis, content gaps
// ============================================================================
export const trendsAPI = {
    // Discover trending topics (used ONLY by TrendingTopics page)
    discoverTrending: (keyword, locationCode, forceRefresh = false, fromRecentSearch = false) =>
        api.post('/trends/discover-trending', {
            keyword,
            location_code: locationCode,
            force_refresh: forceRefresh,
            from_recent_search: fromRecentSearch
        }),

    // Discover trend alerts (used ONLY by TrendAlerts page - does NOT save to recent_searches)
    discoverTrendAlerts: (keyword, locationCode, forceRefresh = false, fromRecentSearch = false) =>
        api.post('/trends/discover-alerts', {
            keyword,
            location_code: locationCode,
            force_refresh: forceRefresh,
            from_recent_search: fromRecentSearch
        }),

    // Get recent searches for the current user (for Trending Topics & Trend Alerts)
    getRecentSearches: () => api.get('/trends/recent-searches'),

    // Get recent searches for Keyword Timing page only
    getTimingRecentSearches: () => api.get('/trends/timing-recent-searches', { params: { page_type: 'keyword_timing' } }),

    // Get recent searches for Keyword Trends Analyzer page only
    getTrendsRecentSearches: () => api.get('/trends/timing-recent-searches', { params: { page_type: 'keyword_trends' } }),

    // Get recent categories for the current user
    getRecentCategories: () => api.get('/trends/recent-categories'),

    // Analyze keyword timing and seasonality (used by KeywordTrends page)
    analyzeTiming: (keyword, locationCode, forceRefresh = false, fromRecentSearch = false, pageType = 'keyword_timing') =>
        api.post('/trends/analyze-timing', {
            keyword,
            location_code: locationCode,
            force_refresh: forceRefresh,
            from_recent_search: fromRecentSearch,
            page_type: pageType
        }),

    // Compare multiple keywords side-by-side (used by KeywordComparison page)
    compareKeywords: (keywords, locationCode, forceRefresh = false) =>
        api.post('/trends/compare', {
            keywords,
            location_code: locationCode,
            force_refresh: forceRefresh
        }),

    // Get recent keyword comparisons (used by KeywordComparison page)
    getCompareRecentSearches: () => api.get('/trends/compare-recent'),

    // Find content gaps (used by ContentGapFinder page)
    findContentGaps: (niche, competitorUrl, locationCode, forceRefresh = false, fromRecentSearch = false) =>
        api.post('/trends/content-gaps', {
            niche: niche,
            competitor_url: competitorUrl,
            location_code: locationCode,
            force_refresh: forceRefresh,
            from_recent_search: fromRecentSearch
        }),

    // Get recent Content Gap searches (used by ContentGapFinder page)
    getContentGapRecentSearches: () => api.get('/trends/content-gaps-recent'),
};

// ============================================================================
// TOPICS API - Topic queue management
// ============================================================================
export const topicsAPI = {
    // Get all topics in queue
    getTopics: () => api.get('/topics'),

    // Delete a single topic
    deleteTopic: (topicId) => api.delete(`/topics/${topicId}`),

    // Delete multiple topics
    deleteTopics: (topicIds) =>
        Promise.all(topicIds.map(id => api.delete(`/topics/${id}`))),

    // Generate content for a single topic
    generateTopic: (topicId) => api.post(`/topics/${topicId}/generate`),

    // Retry a failed topic (clears checkpoint, resets status, redispatches)
    retryTopic: (topicId) => api.post(`/topics/${topicId}/regenerate`),

    // Fetch a single topic by ID
    getTopic: (topicId) => api.get(`/topics/${topicId}`),

    // Generate content for multiple topics
    generateTopics: (topicIds) =>
        Promise.all(topicIds.map(id => api.post(`/topics/${id}/generate`))),

    // Publish a topic
    publishTopic: (topicId, publishType = 'publish') =>
        api.post(`/topics/${topicId}/publish`, { publish_type: publishType }),

    // Bulk publish multiple topics
    bulkPublishTopics: (topicIds, publishType = 'publish') =>
        api.post('/topics/bulk-publish', { topic_ids: topicIds, publish_type: publishType }),

    // Create multiple topics in one request (batch import)
    batchCreateTopics: (data) => api.post('/topics/batch', data),
};

// ============================================================================
// WORDPRESS SITES API - WordPress site management
// ============================================================================
export const sitesAPI = {
    // Get all WordPress sites
    getWordPressSites: () => api.get('/wordpress-sites'),

    // Add a new WordPress site
    addWordPressSite: (siteData) => api.post('/wordpress-sites', siteData),

    // Test WordPress site connection
    testWordPressSite: (siteId) => api.post(`/wordpress-sites/${siteId}/test`),

    // Delete a WordPress site
    deleteWordPressSite: (siteId) => api.delete(`/wordpress-sites/${siteId}`),

    // Get categories for a specific site
    getSiteCategories: (siteId) => api.get(`/wordpress-sites/${siteId}/categories`),

    // Publish article to WordPress
    publishToWordPress: (siteId, articleData) =>
        api.post(`/wordpress-sites/${siteId}/publish`, articleData),
};

// ============================================================================
// DASHBOARD API - Dashboard statistics and activity
// ============================================================================
export const dashboardAPI = {
    // Get dashboard statistics
    getStats: () => api.get('/dashboard/stats'),

    // Get activity log
    getActivityLog: () => api.get('/dashboard/activity-log'),

    // Lightweight pending count for polling (pending + ready-to-publish)
    getPendingCount: () => api.get('/dashboard/pending-count'),
};

// ============================================================================
// PROFILE API - User profile + onboarding
// ============================================================================
export const profileAPI = {
    get: () => api.get('/user/profile'),
    update: (data) => api.put('/user/profile', data),
};

// ============================================================================
// PLATFORM STATS API - Social proof + norm comparison
// ============================================================================
export const platformStatsAPI = {
    get: () => api.get('/platform/stats'),
};

// ============================================================================
// NOTIFICATION API - In-app notification bell
// ============================================================================
export const notificationAPI = {
    getAll: () => api.get('/notifications'),
    markRead: (id) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post('/notifications/read-all'),
};

// ============================================================================
// CONTENT API - Content generation and management
// ============================================================================
export const contentAPI = {
    // Generate content
    generate: (data) => api.post('/content/generate', data),

    // Get a specific article
    getArticle: (id) => api.get(`/content/article/${id}`),

    // List all articles
    listArticles: () => api.get('/articles'),
};

// ============================================================================
// SUBSCRIPTION API - Subscription and usage management
// ============================================================================
export const subscriptionAPI = {
    // Get subscription details
    getSubscription: () => api.get('/subscription'),

    // Get usage statistics
    getUsage: () => api.get('/subscription/usage'),
};

// ============================================================================
// KEYWORD DIFFICULTY API - KD status and refresh
// ============================================================================
export const kdAPI = {
    // Check if new KD data is available
    getStatus: () => api.get('/kd/status'),

    // Refresh KD for specific keywords
    refreshKeywords: (keywords, locationCode = '2840') =>
        api.post('/kd/refresh', {
            keywords,
            location_code: locationCode
        }),
};

// ============================================================================
// AI SEARCH OPTIMIZER API - AI search optimization and llms.txt generation
// ============================================================================
export const aiSearchAPI = {
    // Scan WordPress site for AI readiness (smart adaptive scanning)
    scanSite: (wordPressSiteId) =>
        api.post('/ai-search/scan', {
            wordpress_site_id: wordPressSiteId
        }),

    // Generate llms.txt file
    generateLlmsTxt: (wordPressSiteId) =>
        api.post('/ai-search/generate-llms-txt', { wordpress_site_id: wordPressSiteId }),

    // Generate Schema.org markup
    generateSchema: (wordPressSiteId, schemaTypes) =>
        api.post('/ai-search/generate-schema', { wordpress_site_id: wordPressSiteId, schema_types: schemaTypes }),

    // Push llms.txt content to WordPress site
    pushLlmsTxt: (wordPressSiteId, content) =>
        api.post('/ai-search/push-llms-txt', { wordpress_site_id: wordPressSiteId, content }),

    // Remove llms.txt from WordPress site
    removeLlmsTxt: (wordPressSiteId) =>
        api.post('/ai-search/remove-llms-txt', { wordpress_site_id: wordPressSiteId }),

    // Remove/disable Schema.org markup
    removeSchema: (wordPressSiteId) =>
        api.post('/ai-search/remove-schema', { wordpress_site_id: wordPressSiteId }),

    // Deploy optimizations to WordPress
    deployOptimizations: (scanId, options) =>
        api.post('/ai-search/deploy', {
            scan_id: scanId,
            ...options
        }),

    // Get scan history for a site
    getScanHistory: (siteId) =>
        api.get(`/ai-search/scan-history/${siteId}`),

    // Fix robots.txt — allow blocked AI bots via WordPress bridge
    fixRobotsTxt: (wordPressSiteId, bots) =>
        api.post('/ai-search/fix-robots', { wordpress_site_id: wordPressSiteId, bots }),

    // Block robots.txt — block allowed AI bots via WordPress bridge
    blockRobotsTxt: (wordPressSiteId, bots) =>
        api.post('/ai-search/block-robots', { wordpress_site_id: wordPressSiteId, bots }),
};


// ============================================================================
// RANK ABOVE API - Competitor intelligence + ranking strategy
// ============================================================================
export const rankAboveAPI = {
    // Generate a full competitor intelligence report
    generate: (yourSiteUrl, competitorUrl, locationCode, forceRefresh = false) =>
        api.post('/rank-above/generate', {
            your_site_url: yourSiteUrl,
            competitor_url: competitorUrl,
            location_code: locationCode,
            force_refresh: forceRefresh,
        }),

    // Get recent reports for the current user
    getRecent: () => api.get('/rank-above/recent'),
};

// ============================================================================
// MULTI-LANGUAGE TRENDS API - Cross-language trend discovery
// ============================================================================
export const multiLanguageAPI = {
    // Discover trending topics across non-English markets
    discover: (forceRefresh = false) =>
        api.post('/multilanguage-trends/discover', { force_refresh: forceRefresh }),

    // Get recent discovery history (last 5)
    getRecent: () => api.get('/multilanguage-trends/recent'),

    // Load a specific historical snapshot by ID
    getById: (id) => api.get(`/multilanguage-trends/history/${id}`),
};

// ============================================================================
// GEOGRAPHIC OPTIMIZER API - Geographic interest analysis
// ============================================================================
export const geographicAPI = {
    // Analyze geographic interest for a keyword
    analyze: (keyword, locationCode = '2840', forceRefresh = false) =>
        api.post('/geographic/analyze', {
            keyword,
            location_code: locationCode,
            force_refresh: forceRefresh,
        }),

    // Get recent geographic searches for the current user
    getRecent: () => api.get('/geographic/recent'),
};

// ============================================================================
// Export the base api instance for custom calls if needed
// ============================================================================
export default api;

